import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/platform/AppShell";
import { PageTitle } from "@/components/platform/Badges";
import { ControlDashboard } from "@/components/controls/ControlDashboard";
import { ControlDialog, ControlGrid } from "@/components/controls/ControlGrid";
import { CalendarView, DeficiencyList, EvidenceRepo, ReportsPanel, RequirementsList, TestsList } from "@/components/controls/ControlLists";
import { api } from "@/lib/api";
import { hasPerm, useControlMeta } from "@/lib/controls";

const TABS = [["dashboard", "Dashboard"], ["library", "Control library"], ["calendar", "Execution calendar"], ["testing", "Testing"],
  ["deficiencies", "Deficiencies"], ["requirements", "Evidence requirements"], ["evidence", "Evidence repository"], ["reports", "Reports"]];

export default function ControlLibrary() {
  const meta = useControlMeta();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const [dash, setDash] = useState(null);
  const [open, setOpen] = useState(false);
  const loadDash = useCallback(() => api.get("/control-reports/health").then(({ data }) => setDash(data)).catch(() => {}), []);
  useEffect(() => { loadDash(); }, [loadDash]);
  return (
    <AppShell testId="control-library-page">
      <PageTitle eyebrow="Operational compliance" title="Control Library"
                 subtitle="Define, operate, test, evidence and improve DPDP controls. Operational status is tracked separately and never changes imported App A assessment scores."
                 actions={hasPerm(meta?.my_permissions, "CONTROL_CREATE") && (
                   <Button onClick={() => setOpen(true)} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="new-control-btn"><Plus className="w-4 h-4 mr-1" />New control</Button>)} />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-6">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto" data-testid="control-tabs">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`ctab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="dashboard" className="mt-5"><ControlDashboard d={dash} /></TabsContent>
        <TabsContent value="library" className="mt-5">{meta && <ControlGrid meta={meta} onChanged={loadDash} />}</TabsContent>
        <TabsContent value="calendar" className="mt-5"><CalendarView meta={meta} /></TabsContent>
        <TabsContent value="testing" className="mt-5"><TestsList /></TabsContent>
        <TabsContent value="deficiencies" className="mt-5"><DeficiencyList /></TabsContent>
        <TabsContent value="requirements" className="mt-5"><RequirementsList /></TabsContent>
        <TabsContent value="evidence" className="mt-5"><EvidenceRepo meta={meta} /></TabsContent>
        <TabsContent value="reports" className="mt-5"><ReportsPanel meta={meta} /></TabsContent>
      </Tabs>
      <ControlDialog open={open} onOpenChange={setOpen} meta={meta} onSaved={loadDash} />
    </AppShell>
  );
}
